import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pipette, Upload, Trash2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { BRAND, BRAND_SWATCHES, isDarkEnough } from "@/lib/brand";
import { useCompany } from "@/context/CompanyContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LogoPicker = () => {
  const { company, setCompany } = useCompany();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const run = async (fn, ok) => {
    setBusy(true);
    try { setCompany(await fn()); toast.success(ok); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update the logo"); }
    finally { setBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5 MB"); return; }
    run(() => api.uploadCompanyLogo(f), "Logo updated");
  };

  return (
    <div className="space-y-2">
      <Label>Logo</Label>
      <div className="flex items-center gap-3">
        <div className="h-14 w-28 shrink-0 rounded-md border border-border bg-white flex items-center justify-center overflow-hidden" data-testid="company-logo-preview">
          <img src={company?.logo_url ? `${BACKEND}${company.logo_url}` : BRAND.mark} alt="Logo preview"
            className={company?.logo_url ? "max-h-12 max-w-[104px] object-contain" : "h-10"} />
        </div>
        <div className="flex flex-col items-start gap-1.5">
          <Button type="button" size="sm" variant="outline" className="rounded-full" disabled={busy}
            onClick={() => fileRef.current?.click()} data-testid="company-logo-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
            {company?.logo_url ? "Change logo" : "Upload logo"}
          </Button>
          {company?.logo_url && (
            <button type="button" disabled={busy} onClick={() => run(api.removeCompanyLogo, "Logo removed")}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors" data-testid="company-logo-remove-btn">
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="company-logo-file-input" />
      </div>
      <p className="text-xs text-muted-foreground">PNG or JPG under 5 MB. A wide logo on a white or clear background works best.</p>
    </div>
  );
};

const ColorPicker = ({ color, setColor }) => (
  <div className="space-y-2">
    <Label>Main colour</Label>
    <div className="flex flex-wrap gap-2" data-testid="company-color-swatches">
      {BRAND_SWATCHES.map((c) => {
        const on = (color || "").toUpperCase() === c;
        return (
          <button key={c} type="button" aria-label={`Colour ${c}`} onClick={() => setColor(c)} data-testid={`company-color-${c.slice(1)}`}
            style={{ background: c }}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-110 ring-offset-2 ring-offset-background ${on ? "ring-2 ring-foreground" : ""}`}>
            {on && <Check className="w-4 h-4 text-white" />}
          </button>
        );
      })}
      <label title="Custom colour" className="relative w-8 h-8 rounded-full border border-dashed border-muted-foreground/50 flex items-center justify-center cursor-pointer hover:bg-secondary transition-colors">
        <Pipette className="w-4 h-4 text-muted-foreground" />
        <input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} data-testid="company-color-custom"
          className="absolute inset-0 opacity-0 cursor-pointer" />
      </label>
    </div>
    {!isDarkEnough(color) && (
      <p className="text-xs text-destructive" data-testid="company-color-warning">Too light: white text won't be readable. Pick a darker shade.</p>
    )}
  </div>
);

export const CompanyBrandingSection = ({ name, setName, color, setColor }) => (
  <div className="rounded-lg border border-border p-4 space-y-4" data-testid="company-branding-section">
    <div>
      <p className="text-sm font-medium">Company branding</p>
      <p className="text-xs text-muted-foreground">Your crew sees this name, logo and colour in the app. It's also used on PDFs and emails.</p>
    </div>
    <div className="space-y-2">
      <Label>Company name</Label>
      <Input data-testid="company-name-input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Your company name" />
    </div>
    <LogoPicker />
    <ColorPicker color={color} setColor={setColor} />
  </div>
);
